<template>
	<div id="top">
		<nav class="main-nav fix-on-scroll" :class="{ 'show-mobile-nav': showMobileNav }">
			<div class="row">
				<div class="columns column12">
					<div class="nav-bar">
						<nuxt-link
							class="logo"
							:to="{ path: localePath('/', locale), hash: route.name.startsWith('index') && '#top' }"
						/>

						<div class="nav-content">
							<ul>
								<li class="menu-close" @click="hideMobileNav">
									<font-awesome-icon class="icon" icon="fal fa-times" size="1x" />
								</li>
								<li v-for="item in topmenu[locale]" :key="item.filename" @click="hideMobileNav">
									<nuxt-link :to="item.filename">
										{{ item.header }}
									</nuxt-link>
								</li>
								<li class="lang-nav mobile-only">
									<div v-for="item in langmenu" :key="item.filename" class="language">
										<nuxt-link
											class="flag"
											:class="item.language"
											:to="item.filename"
											@click="hideMobileNav"
										>
											<span>{{ item.language }}</span>
										</nuxt-link>
									</div>
								</li>
							</ul>
						</div>
						<div v-if="page" class="language-navigation" @click="toggleLanguageMenu">
							<div v-if="langmenu.length" v-click-outside="closeLanguageMenu" class="lang-select">
								<div class="flag active" :class="locale" />
								<span>{{ locale }}</span>
								<font-awesome-icon :icon="`fal fa-chevron-down`" size="1x" />
								<div class="lang-wrapper" :class="{ active: showLanguageMenu }">
									<div v-for="item in langmenu" :key="item.filename" class="language">
										<nuxt-link
											class="flag"
											:class="item.language"
											:to="item.filename"
											@click="hideMobileNav"
										>
											<span>{{ item.language }}</span>
										</nuxt-link>
									</div>
								</div>
							</div>
						</div>
						<div class="buttons">
							<div class="menu-button mobile-only" @click="toggleMobileNav">
								<font-awesome-icon class="icon" icon="fal fa-bars" size="1x" />
								<span>{{ $t('buttonMenu') }}</span>
							</div>
							<book-button class="button book-now cta reserve-now">
								<font-awesome-icon class="icon" icon="fal fa-calendar-alt" size="1x" />
								<span>{{ defaults[locale].website.book }}</span>
							</book-button>
						</div>
					</div>
				</div>
			</div>
		</nav>
	</div>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();

await fetchPage();
await fetchDefaults();

const showMobileNav = ref(false);
const showLanguageMenu = ref(false);

const { data: topmenu } = await useWebsiteFetch('topmenu');

const langmenu = computed(() => {
	return (page.value && page.value.langmenu) ?? [];
});

const toggleMobileNav = () => {
	showMobileNav.value = !showMobileNav.value;
};

const hideMobileNav = () => {
	showMobileNav.value = false;
};

const toggleLanguageMenu = () => {
	showLanguageMenu.value = !showLanguageMenu.value;
};

const closeLanguageMenu = () => {
	showLanguageMenu.value = false;
};
</script>

<style lang="scss" scoped>
.mobile-only {
	display: none !important;
}

.main-nav {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	background: var(--secondary-background-color);
	box-shadow: 0 0 20px rgb(0 0 0 / 10%);

	ul {
		display: inline-block;
		list-style: none;

		li {
			display: inline-block;
		}
	}
}

.nav-bar {
	display: flex;
	align-items: center;
	padding: 16px 0;
	z-index: 9;
	position: relative;

	.buttons {
		display: flex;
		align-items: center;
		gap: 18px;

		.button {
			padding: 10px 30px;
			margin: 0;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.icon {
			display: none;
			margin-right: 5px;
		}
	}
}

.logo {
	padding: 0;
	text-decoration: none;
	margin: 0;
	background: url('~/assets/images/logo-depijp-full-groen.png') no-repeat center center #fff;
	width: 190px;
	height: 195px;
	background-size: 160px;
	position: absolute;
	inset: 0 auto auto 0;
	transition: all 0.3s ease-in-out;
	box-shadow: 0 0 20px rgb(0 0 0 / 10%);
}

.fixed {
	.logo {
		height: 80px;
		background-position: top center;
		box-shadow: none;
	}
}

.nav-content {
	margin: auto;

	a {
		color: var(--nav-color);
		text-decoration: none;
		margin: 0 18px;
		letter-spacing: 1px;
		font-weight: 400;
		transition: color 0.3s ease-in-out;
		font-size: var(--body-font-size);
		font-family: var(--subtitle-font-family);

		&.active,
		&:hover,
		&.router-link-active,
		&.router-link-exact-active {
			color: var(--cta-color);
		}
	}

	.menu-close {
		color: #000;
		display: none;
		font-size: 30px;
		cursor: pointer;
		position: absolute;
		top: 15px;
		width: auto;
		right: 20px;
		padding: 0;
		margin: 0;
	}
}

.lang-nav,
.lang-select {
	margin: 0 0 25px;

	.language {
		margin: 20px 0;
	}

	img {
		width: 16px;
		float: left;
		margin: 0 5px 0 0;
	}

	a {
		width: 100%;
		float: left;
		color: var(--body-color);
		text-decoration: none;
		margin: 4px 0;
		text-transform: uppercase;

		&.active {
			color: var(--cta-color);
		}
	}
}

.flag::before {
	content: '';
	width: 18px;
	position: relative;
	top: -2px;
	height: 20px;
	margin-right: 6px;
	display: inline-block;
	background-size: 18px;
}

.flag.en::before {
	background: url('~/assets/images/en.svg') no-repeat center center;
	background-size: 18px;
}

.flag.nl::before {
	background: url('~/assets/images/nl.svg') no-repeat center center;
	background-size: 18px;
}

.flag.de::before {
	background: url('~/assets/images/de.svg') no-repeat center center;
	background-size: 18px;
}

.flag.fr::before {
	background: url('~/assets/images/fr.svg') no-repeat center center;
	background-size: 18px;
}

.flag.es::before {
	background: url('~/assets/images/es.svg') no-repeat center center;
	background-size: 18px;
}

.flag.it::before {
	background: url('~/assets/images/it.svg') no-repeat center center;
	background-size: 18px;
}

.flag.active {
	&::before {
		top: 2px;
	}
}

.language-navigation:hover .lang-nav-sel,
.language-navigation:hover .fa-globe {
	color: var(--cta-color);
}

.lang-nav-sel {
	box-shadow: none;
	cursor: pointer;
	width: 30px;
	padding: 0 4px 0 0;
	color: #fff;
	border: none;
	text-transform: uppercase;
	font-size: 12px;
	font-family: sans-serif;
	appearance: none;
	background: url('~/assets/images/dropdown.png') right 1px center no-repeat;
	background-size: 7px;
	transition: color 0.3s ease-in-out;

	option {
		color: var(--body-color);
	}

	&::-ms-expand {
		display: none;
	}
}

.lang-select {
	padding: 8px 20px;
	margin: 0 10px;

	/* border-left: 1px solid #e5e5e5; */
	position: relative;
	cursor: pointer;
	text-transform: uppercase;
	font-size: 13px;
	display: flex;
	flex-flow: row nowrap;
	align-items: center;

	span {
		margin: 0 4px;
	}

	a {
		color: #000;
		display: flex;
		flex-flow: row nowrap;
		gap: 8px;
		margin: 7px 0;
		place-content: center flex-start;
		width: 100%;
		text-decoration: none;
	}

	svg {
		font-size: 12px;
	}

	.language {
		margin: 0;

		a.flag {
			margin: 2px 0;
			flex-flow: row nowrap;
			place-content: center flex-start;
			gap: 0;
		}
	}
}

.lang-wrapper {
	display: none;
	position: absolute;
	top: 35px;
	left: 3px;
	right: 3px;
	padding: 10px 17px;
	text-align: center;
	background: var(--secondary-background-color);
	box-shadow: 0 18px 18px rgb(0 0 0 / 10%);
	z-index: 90;

	&.active,
	a {
		display: flex;
		flex-flow: column nowrap;
		place-content: space-between center;
		color: #000 !important;
		font-size: 13px;
		gap: 5px;
	}

	a {
		width: 100%;
		flex-direction: row;

		span {
			font-size: 13px;
		}
	}

	img {
		width: 16px;
		height: 16px;
		margin: 2px 0 0;
	}
}

.language-navigation .fa-globe {
	margin: 0 2px 0 0;
	color: var(--body-color);
	transition: color 0.3s ease-in-out;
}

@media (min-width: 1201px) {
	.nav-content {
		padding: 0 0 0 250px;
	}
}

@media (max-width: 1280px) {
	.nav-bar a {
		font-size: 14px;
	}
}

@media (max-width: 1200px) {
	.menu-button {
		text-transform: uppercase;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: space-between;
		margin: 0 10px 0 0;
		cursor: pointer;

		svg {
			font-size: 22px;
		}

		span {
			margin: 0 0 0 5px;
		}
	}

	.mobile-only {
		display: flex !important;

		&.lang-nav {
			display: inline-block !important;
			margin: 30px 0 0;
			padding: 0 10px;
		}
	}

	.lang-nav {
		.language {
			.flag::before {
				top: 3px;
			}
		}
	}

	.language-navigation {
		display: none;
	}

	.nav-bar .buttons {
		.menu-button {
			.icon {
				display: unset;
			}
		}
	}

	.main-nav {
		position: fixed !important;
		top: 0;
		box-shadow: 0 0 10px rgb(0 0 0 / 10%);

		&.show-mobile-nav {
			.nav-content ul {
				display: flex;
				flex-direction: column;
				justify-content: center;
				opacity: 1;
				visibility: visible;
				transition: opacity 0.3s ease-in-out;

				li:not(.menu-close) {
					width: 100%;
				}

				li:not(.lang-nav, .menu-close) {
					margin: 10px 0;

					a {
						font-size: 28px;
						font-family: var(--heading-font-family);
						font-weight: 700;
						text-transform: none;
					}
				}
			}
		}

		ul {
			position: fixed;
			inset: 0;
			background: rgb(255 255 255 / 90%);
			backdrop-filter: blur(7px);
			text-align: center;
			padding: 20px;
			opacity: 0;
			visibility: hidden;
			overflow-y: scroll;
		}
	}

	.nav-content .menu-close {
		display: block;
	}
}

@media (max-width: 880px) {
	.nav-bar {
		padding: 14px 0;

		.buttons {
			.button {
				padding: 16px;

				.icon {
					display: unset;
				}
			}

			.menu-button {
				margin-right: 5px;

				.icon {
					font-size: 22px;
				}
			}

			.icon {
				margin-right: 0;
				font-size: 16px;
			}

			span {
				display: none;
			}
		}
	}
}

@media (max-width: 580px) {
	.nav-bar .buttons span {
		display: none;
	}

	.logo {
		background: url('~/assets/images/logo-depijp-small-groen.png') no-repeat center center #fff;
		width: 150px;
		height: 155px;
		background-size: 120px;
	}

	.fixed {
		.logo {
			background-position: center top 10px;
			background-size: 130px;
		}
	}
}
</style>
